<template>
  <v-dialog
    v-model="show"
    scrollable
    max-width="500px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        icon
        v-on="on"
      >
        <v-icon>{{ canEdit ? 'mdi-account-multiple-plus' : 'mdi-account-multiple' }}</v-icon>
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        Travelers
      </v-card-title>

      <v-divider />

      <v-card-text class="pb-0">
        <v-text-field
          v-if="canEdit"
          v-model.trim="userEmail"
          label="Invite a traveler"
          placeholder="me@vjago.com"
          outlined
          append-icon="mdi-account-plus"
          type="email"
          class="mt-4"
          @click:append="inviteUser"
        />
      </v-card-text>

      <v-list>
        <!-- Invited users -->
        <v-list-item
          v-for="invitation in invited"
          :key="`invited-${invitation.email}`"
        >
          <v-list-item-avatar class="primary">
            <span class="white--text">{{ invitation.email[0].toUpperCase() }}</span>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title>
              <v-chip
                outlined
                small
              >
                Invited
              </v-chip>
            </v-list-item-title>
            <v-list-item-subtitle>{{ invitation.email }}</v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-btn
              icon
              @click="deleteInvitation(invitation.id)"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>

        <!-- users added to the trip -->
        <v-list-item
          v-for="user in travelers"
          :key="`user-${user.id}`"
          two-line
        >
          <v-list-item-avatar class="primary">
            <v-img
              v-if="user.photoURL"
              :src="user.photoURL"
            />

            <span
              v-else-if="user.displayName"
              class="white--text"
            >{{ user.displayName[0] }}</span>

            <span v-else>🧑</span>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title>
              {{ user.displayName || user.email }}
              <v-chip x-small>
                <v-icon
                  left
                  small
                >
                  {{ allRoles.find(r => r.value === user.role).icon }}
                </v-icon>
                {{ user.role }}
              </v-chip>
            </v-list-item-title>

            <v-list-item-subtitle v-if="user.displayName">
              {{ user.email }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-menu
              v-if="user.id !== trip.$.createdBy && $currentUser.id === trip.$.createdBy"
              bottom
              left
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  v-on="on"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list
                :disabled="trip.loading || trip.saving || trip.deleting"
              >
                <v-subheader>ROLE</v-subheader>

                <v-list-item
                  @click="changeRole(user, 'viewer')"
                >
                  <v-list-item-avatar>
                    <v-icon>mdi-eye</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title>
                      Viewer
                    </v-list-item-title>

                    <v-list-item-subtitle>
                      Can view the trip
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-avatar>
                    <v-icon
                      v-if="trip.$.users[user.id] === 'viewer'"
                      color="success"
                    >
                      mdi-check
                    </v-icon>
                  </v-list-item-avatar>
                </v-list-item>

                <v-list-item
                  @click="changeRole(user, 'editor')"
                >
                  <v-list-item-avatar>
                    <v-icon>mdi-pencil</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title>
                      Editor
                    </v-list-item-title>

                    <v-list-item-subtitle>
                      Can edit name, steps, but not delete
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-avatar>
                    <v-icon
                      v-if="trip.$.users[user.id] === 'editor'"
                      color="success"
                    >
                      mdi-check
                    </v-icon>
                  </v-list-item-avatar>
                </v-list-item>

                <v-divider />

                <v-list-item
                  @click="deleteUser(user)"
                >
                  <v-list-item-avatar>
                    <v-icon color="error">
                      mdi-delete-forever
                    </v-icon>
                  </v-list-item-avatar>

                  <v-list-item-title>
                    Remove traveler
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <div class="text-center ma-6">
        <div class="pb-2">
          <v-btn-toggle
            v-model="trip.visibility"
            mandatory
            rounded
            color="accent"
            @change="trip.save()"
          >
            <v-btn value="private">
              <v-icon left>
                mdi-lock
              </v-icon>
              <span>Private</span>
            </v-btn>

            <v-btn value="public">
              <v-icon left>
                mdi-eye
              </v-icon>
              <span>Public</span>
            </v-btn>
          </v-btn-toggle>
        </div>

        <small
          v-if="trip.visibility && trip.visibility === 'public'"
          class="grey--text"
        >
          Your trip is visible to everyone,
          but it can be modified only by people you specified.
        </small>

        <small
          v-else
          class="grey--text"
        >
          Your trip is visible only to people you invited
          and can be modified only by those you specified.
        </small>
      </div>

      <v-divider />

      <v-card-actions>
        <v-spacer />

        <v-btn
          color="primary"
          text
          @click="show = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { firestore } from '@/plugins/firebase'

export default {
  props: {
    trip: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      allRoles: [
        { text: 'Viewer', value: 'viewer', icon: 'mdi-eye' },
        { text: 'Editor', value: 'editor', icon: 'mdi-pencil' },
        { text: 'Owner', value: 'owner', icon: 'mdi-seat' }
      ],
      invited: [],
      show: false,
      travelers: [],
      userEmail: null
    }
  },
  computed: {
    travelersCount () {
      return Object.keys(this.trip.users).length
    },
    canEdit () {
      if (this.$currentUser) {
        const role = this.trip.users[this.$currentUser.id]
        return role === 'owner' || role === 'editor'
      } else {
        return false
      }
    }
  },
  watch: {
    '$route' () {
      this.fetchTravelers()
    }
  },
  mounted () {
    this.fetchTravelers()
  },
  methods: {
    inviteUser () {
      if (!this.userEmail) { return }

      firestore
        .collection('invitations')
        .add({
          createdBy: this.$currentUser.id,
          email: this.userEmail,
          trip: this.trip.id
        })
        .then(() => {
          this.userEmail = null
        })
    },
    async deleteUser (user) {
      delete this.trip.users[user.id]
      await this.trip.save()
    },
    deleteInvitation (id) {
      firestore
        .collection('invitations')
        .doc(id)
        .delete()
    },
    async changeRole (user, role) {
      this.trip.users[user.id] = role
      await this.trip.save()
    },
    async fetchTravelers () {
      if (!this.trip.id) { return }

      this.travelers = []

      for (const uid in this.trip.users) {
        let user = await firestore.collection('users').doc(uid).get()
        if (!user.exists) { continue }

        this.travelers.push({
          id: user.id,
          ...user.data(),
          role: this.trip.users[uid]
        })
      }

      firestore.collection('invitations')
        .where('createdBy', '==', this.$currentUser.id)
        .onSnapshot(snapshot => {
          this.invited = []

          snapshot.forEach(doc => {
            if (doc.data().trip === this.trip.id) {
              this.invited.push({
                id: doc.id,
                ...doc.data()
              })
            }
          })
        })
    }
  }
}
</script>

<style>

</style>
